import {Card, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {TextBox} from '../../../Core/UI';
import React from 'react';
import {observer} from 'mobx-react-lite';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {Health} from '../../../Core/mobx/ParameterStore/Health';
import {Approval} from '../../../Core/mobx/ParameterStore/Approval';
import BehaviorQuestions from '../../Questions/Behaviour';
import HealthQuestionsList from '../../PreQualification/HealthQuestions/HealthQuestionsList';
import {CategoryCard} from '../CategoryCard';
import {AdditionalCondition, Medication} from '../../types';
import {SelectInputElement} from '../../PreQualification/Components/SelectInputElement';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/ApprovalXref';

const useStyles = makeStyles({
    icon: {
        marginRight: 4,
    },
    approved: {
        color: 'green',
    },
    denied: {
        color: 'red',
    },
    sectionTitle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 46,
        width: 46,
        borderRadius: '50%',
        backgroundColor: '#e8edec',
        color: '#456d69',
        marginRight: 21,
        fontSize: 26,
        textAlign: 'center',
    },
    sectionTitleWrapper: {
        display: 'flex',
        alignItems: 'center',
        textTransform: 'capitalize',
    },
});

type HealthInfoProps = {
    valueChanged: (p?: Parameter, value?: any) => void;
    parameters: Approval;
    setCategory: (entry: string) => void;
    healthParameters: Health;
    index: number;
};

const HealthInfo = observer(({valueChanged, parameters, setCategory, healthParameters, index}: HealthInfoProps) => {
    const classes = useStyles();

    const required = (value?: string) => (
        <span>
            {value && value} <span style={{color: 'red'}}>*</span>
        </span>
    );

    const containsAdditionalConditions = parameters.additionalConditions.value.containsAdditionalConditions;
    const conditions = parameters.additionalConditions.value.conditions;

    return (
        <CategoryCard title={'Health'} setCategory={setCategory} index={index}>
            <Grid item xs={12}>
                <Card style={{padding: 20}}>
                    <Grid item xs={4}>
                        <strong>RX Self-Disclosure</strong>
                    </Grid>
                    <Grid item xs={4} style={{margin: '20px 0'}}>
                        <p>Have you been prescribed any medications in the past 12 months?</p>
                        <p>Answer: {containsAdditionalConditions ? 'YES' : 'NO'}</p>
                    </Grid>
                    {containsAdditionalConditions && (
                        <Grid container spacing={1}>
                            <TableContainer style={{maxHeight: '500px', overflow: 'auto'}}>
                                <Table style={{minWidth: '100%'}}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Condition Name</TableCell>
                                            <TableCell>Prescribed Medication</TableCell>
                                            <TableCell>Dosage</TableCell>
                                            <TableCell>Frequency</TableCell>
                                            <TableCell>Last Dose</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {conditions?.flatMap((condition: AdditionalCondition) =>
                                            condition.medications.map((medication: Medication, i: number) => {
                                                const isFirst = i === 0;
                                                const isLast = i === condition.medications.length - 1;
                                                const styles = !isLast ? {borderBottom: 0} : {};

                                                return (
                                                    <TableRow>
                                                        <TableCell style={styles}>
                                                            {isFirst ? condition.name : ''}
                                                        </TableCell>
                                                        <TableCell style={styles}>{medication.name}</TableCell>
                                                        <TableCell style={styles}>
                                                            {medication.dosageQuantity} {medication.dosageUnit}
                                                        </TableCell>
                                                        <TableCell style={styles}>{medication.frequency}</TableCell>
                                                        <TableCell style={styles}>{medication.lastDoseDate}</TableCell>
                                                    </TableRow>
                                                );
                                            }),
                                        )}
                                        {conditions?.length === 0 && (
                                            <TableRow>
                                                <TableCell>No Answer</TableCell>
                                                <TableCell>No Answer</TableCell>
                                                <TableCell>No Answer</TableCell>
                                                <TableCell>No Answer</TableCell>
                                                <TableCell>No Answer</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    )}
                </Card>
            </Grid>
            <Grid item xs={12}>
                <TextBox
                    id={'medicationHistory'}
                    label={required('RX Notes: Medication, Condition, Dosage, Frequency, Beginning Date of Medication')}
                    value={parameters.medicationHistory?.value || 'NA'}
                    multiline
                    rows={10}
                    onChange={(value) => valueChanged(parameters.medicationHistory, value)}
                />
            </Grid>
            <Grid item xs={12}>
                <div style={{fontSize: 20, color: '#5D6CC1', fontWeight: 'bold', marginTop: '1em'}}>Last 10 Years</div>
            </Grid>
            <BehaviorQuestions parameters={parameters} valueChanged={valueChanged} preapproval />
            {healthParameters && <HealthQuestionsList parameters={healthParameters} changed={valueChanged} />}
            {parameters.extremeSports.value && (
                <Grid item xs={4}>
                    <SelectInputElement
                        param={{
                            label: 'What extreme sport do you participate in',
                            name: 'extremeSportsOptions',
                            options: fieldOptions.extremeSportsOptions.options,
                            ...parameters.extremeSportsOptions,
                        }}
                        required={required('')}
                        valueChanged={valueChanged}
                        multiple
                    />
                </Grid>
            )}
            {parameters.extremeSportsOptions.value && parameters.extremeSportsOptions.value.includes('ScubaDiving') && (
                <>
                    <div style={{width: '100%'}} />
                    <Grid item xs={4}>
                        <SelectInputElement
                            param={{
                                label: 'Type of Scuba Diving',
                                name: 'extremeSportsScubaType',
                                options: fieldOptions.extremeSportsScubaType.options,
                                ...parameters.extremeSportsScubaType,
                            }}
                            required={required('')}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'extremeSportsScubaDepth'}
                            label={required('How deep do you typically dive (ft)?')}
                            type="number"
                            value={parameters.extremeSportsScubaDepth?.value}
                            onChange={(value) => valueChanged(parameters.extremeSportsScubaDepth, value)}
                        />
                    </Grid>
                </>
            )}
        </CategoryCard>
    );
});

export default HealthInfo;
